import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  inject,
  input,
  OnInit,
} from "@angular/core";
import {AiChatFacade} from "./ai-chat.facade";
import {ChatClientService} from "src/app/services/AIReports/chat-client.service";
import {TranslateModule} from '@ngx-translate/core';
import {DialogBaseComponent} from "src/app/components/common/dialog/dialog-base/dialog-base.component";
import {DialogService} from "src/app/services/UI/dialog.service";
import {ChatReportDisplayComponent} from "./components/chat-report-display/chat-report-display.component";
import {ChatSidebarComponent} from "./components/chat-sidebar/chat-sidebar.component";
import {CommonModule} from "@angular/common";


@Component({
  selector: "app-chat",
  templateUrl: "./chat.component.html",
  styleUrl: "./chat.component.scss",
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ChatReportDisplayComponent,
    ChatSidebarComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ChatClientService],
})
export class ChatComponent extends DialogBaseComponent implements OnInit {
  /**
   * The initial prompt to display in the chat.
   * - `undefined`: Tab-browsing (persist existing chat state).
   * - `null`: Blank template selected (force reset to empty chat).
   * - `string`: Template selected (force reset and pre-fill with this text).
   */
  readonly initialPrompt = input<string | null | undefined>(undefined);

  // ── Layout state ─────────────────────────────────────────────────────────────
  isCollapsed = false;
  isSmallScreen = false;
  isMobileOpen = false;

  // ── Facade (owns all chat state) ──────────────────────────────────────────────
  private readonly facade = inject(AiChatFacade);

  constructor(
    dialogService: DialogService,
    ref: ChangeDetectorRef
  ) {
    super(dialogService, ref);
    this.updateScreenFlags();
  }

  async ngOnInit() {
    const p = this.initialPrompt();
    const currentThread = this.facade.activeThreadId();

    if (p !== undefined || !currentThread) {
      await this.facade.newThread();
      if (p !== null) {
        this.facade.draft.set(p || '');
      }
    }
  }

  @HostListener("window:resize")
  onResize(): void {
    this.updateScreenFlags();
  }

  togglePanel(): void {
    this.isCollapsed = !this.isCollapsed;
    if (this.isSmallScreen) {
      this.isMobileOpen = !this.isCollapsed;
    }
  }

  openMobile(): void {
    this.isMobileOpen = true;
    this.isCollapsed = false;
  }

  closeMobile(): void {
    this.isMobileOpen = false;
  }

  private updateScreenFlags(): void {
    this.isSmallScreen = window.innerWidth <= 767;
    if (!this.isSmallScreen) this.isMobileOpen = false;
  }

  async cancel() {
    await this.facade.newThread();
    this.close();
  }
}
